import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { submitLead } from '../lib/leads'
import { GOALS, reachGoal } from '../lib/metrika'

/**
 * Окно «Напомнить позже»: пользователь оставляет почту, чтобы вернуться
 * к регистрации потом. Заявка уходит в бэкенд RIS (см. lib/leads) и приходит
 * письмом на почту команды.
 *
 * Компонент рендерится только когда окно открыто (см. CtaFinal / HowItWorks):
 * так форма и экран «спасибо» сбрасываются сами, без ручной очистки состояния.
 */
export function ReminderModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const inputRef = useRef<HTMLInputElement>(null)
  const titleId = useId()
  const sent = status === 'sent'

  // Esc закрывает окно, фокус уводим в поле ввода, фон не скроллим.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    inputRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (status === 'sending') return
    setStatus('sending')
    const ok = await submitLead(email)
    // Ошибку тоже отправляем целью: иначе провал формы выглядит в отчётах
    // так же, как «никто не захотел оставлять почту».
    reachGoal(ok ? GOALS.reminderSent : GOALS.reminderError)
    setStatus(ok ? 'sent' : 'error')
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="absolute inset-0 bg-[#041527]/45 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-[440px] rounded-[24px] bg-white p-6 text-left sm:p-8"
        style={{
          boxShadow: '0 24px 60px -12px rgba(4,21,39,0.35)',
          animation: 'modalIn 0.28s cubic-bezier(0.45,0,0.15,1)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-[20px] leading-none text-[#676b94] transition-colors hover:bg-[#f4f5f7] hover:text-[#041527]"
        >
          ×
        </button>

        {sent ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span aria-hidden className="text-[40px] leading-none">
              ✉️
            </span>
            <p id={titleId} className="font-display text-[22px] font-semibold leading-7 text-[#041527]">
              Напомним на&nbsp;почту
            </p>
            <p className="font-body text-[15px] leading-6 text-[#52525b]">
              Письмо со&nbsp;ссылкой на&nbsp;регистрацию придёт на&nbsp;{email}.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 inline-flex items-center justify-center rounded-full bg-[#f4f5f7] px-6 py-3 font-sans text-[16px] font-medium leading-6 text-[#041527] transition-colors hover:bg-[#eceef2]"
            >
              Хорошо
            </button>
          </div>
        ) : (
          <>
            <span aria-hidden className="mb-4 flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#eef2ff] text-[22px] leading-none">
              ⏰
            </span>
            <p id={titleId} className="font-display text-[22px] font-semibold leading-7 text-[#041527] sm:text-[26px] sm:leading-8">
              Напомнить позже
            </p>
            <p className="font-body mt-2 text-[15px] leading-6 text-[#52525b]">
              Оставьте почту&nbsp;— пришлём ссылку на&nbsp;регистрацию, чтобы вернуться к&nbsp;ней,
              когда будет удобно.
            </p>

            <form className="mt-5 flex flex-col gap-3" onSubmit={submit}>
              <input
                ref={inputRef}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ru"
                autoComplete="email"
                disabled={status === 'sending'}
                className="font-body h-[52px] w-full rounded-[14px] border border-[#e9e9ec] bg-[#fafbfc] px-4 text-[16px] leading-6 text-[#041527] outline-none transition-colors placeholder:text-[#9ca3b8] focus:border-[#145aff] focus:bg-white disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={status === 'sending'}
                className="inline-flex h-[52px] items-center justify-center rounded-full px-6 font-sans text-[17px] font-medium leading-6 tracking-[-0.2px] text-white transition duration-300 ease-out hover:brightness-110 active:brightness-90 disabled:cursor-default disabled:opacity-70 disabled:hover:brightness-100"
                style={{ background: 'linear-gradient(180deg, #0d6fe0 0%, #145aff 100%)' }}
              >
                {status === 'sending' ? 'Отправляем…' : 'Напомнить'}
              </button>

              {/* role=alert: ошибку должен озвучить скринридер, она появляется
                  уже после того, как фокус ушёл на кнопку. */}
              {status === 'error' && (
                <p role="alert" className="font-body text-[13px] leading-5 text-[#c0392b]">
                  Не удалось отправить заявку. Попробуйте ещё раз или напишите на{' '}
                  <a href="mailto:info@ris.promo" className="underline decoration-from-font">
                    info@ris.promo
                  </a>
                  .
                </p>
              )}

              {/* Дисклеймер обязателен: форма собирает персональные данные.
                  Формулировка и документы — те же, что в форме на about.ris.promo. */}
              <p className="font-body text-[12px] leading-[18px] text-[#8a8f9e]">
                Нажимая на&nbsp;кнопку, вы даёте{' '}
                <a
                  href="/pdf/processing_of_personal_data.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-from-font hover:text-[#5c6376]"
                >
                  согласие на обработку персональных данных
                </a>{' '}
                и&nbsp;соглашаетесь с&nbsp;условиями{' '}
                <a
                  href="/pdf/privacy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-from-font hover:text-[#5c6376]"
                >
                  Политики конфиденциальности
                </a>
                .
              </p>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
